import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { settingsApi, vehicleTypesApi, usersApi, reportsApi, auditApi, systemApi, spacesApi, revenueApi, productsApi } from '../services/api';
import { formatCurrency, formatDuration, formatDateTime, formatApiError } from '../utils/helpers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useRealtimeSync, ConnectionIndicator } from '../hooks/useRealtimeSync';
import { 
  Gear, Users, CurrencyCircleDollar, ChartBar, ClockCounterClockwise, 
  ArrowLeft, Warning, Download, Trash, Plus, Pencil, Check, X,
  CarSimple, Motorcycle, Bicycle, TrendUp, Calendar, Funnel, ShoppingCart,
  ToggleLeft, ToggleRight, Printer, ArrowCounterClockwise, Article
} from '@phosphor-icons/react';
import PosDiagnostics from '../components/PosDiagnostics';
import AdminCorrections from '../components/AdminCorrections';
import AdminTicketDesigner from '../components/AdminTicketDesigner';

const VEHICLE_ICONS = {
  'Auto': CarSimple,
  'Moto': Motorcycle,
  'Bicicleta': Bicycle
};

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('revenue');
  const [settings, setSettings] = useState({});
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState(null);
  const [detailedReports, setDetailedReports] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [revenueDashboard, setRevenueDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Report filters
  const [reportPeriod, setReportPeriod] = useState('daily');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [filterOperator, setFilterOperator] = useState('');
  const [filterVehicleType, setFilterVehicleType] = useState('');

  // User management
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', password: '', name: '', role: 'operator' });

  // Product management
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', price: 0, category: 'product', active: true, display_order: 0 });

  // ── Real-time sync ──
  const refreshRevenue = useCallback(async () => {
    try {
      const [revenueRes, logsRes] = await Promise.all([
        revenueApi.getDashboard(),
        auditApi.getLogs(100)
      ]);
      setRevenueDashboard(revenueRes.data);
      setAuditLogs(logsRes.data);
    } catch {}
  }, []);

  const { status: realtimeStatus } = useRealtimeSync({
    enabled: !!user,
    onSpaceUpdate: refreshRevenue,
    onChargeUpdate: refreshRevenue,
    onConfigUpdate: () => loadAllData(),
    onSystemReset: () => loadAllData()
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [settingsRes, typesRes, usersRes, reportsRes, logsRes, revenueRes, productsRes] = await Promise.all([
        settingsApi.get(),
        vehicleTypesApi.getAll(),
        usersApi.getAll(),
        reportsApi.get({ period: 'daily' }),
        auditApi.getLogs(100),
        revenueApi.getDashboard(),
        productsApi.getAll()
      ]);
      setSettings(settingsRes.data);
      setVehicleTypes(typesRes.data);
      setUsers(usersRes.data);
      setReports(reportsRes.data);
      setAuditLogs(logsRes.data);
      setRevenueDashboard(revenueRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 3000);
  };

  // Settings handlers
  const handleSettingsChange = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      showMessage('Configuracion guardada');
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  const regenerateSpaces = async () => {
    if (!window.confirm(`Regenerar ${settings.total_spaces} espacios? Esto eliminara los espacios actuales.`)) return;
    setSaving(true);
    try {
      await spacesApi.regenerate(settings.total_spaces);
      showMessage('Espacios regenerados');
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  // Vehicle type handlers
  const handleVehicleTypeChange = (typeId, field, value) => {
    setVehicleTypes(prev => prev.map(t => t.id === typeId ? { ...t, [field]: value } : t));
  };

  const saveVehicleType = async (type) => {
    setSaving(true);
    try {
      await vehicleTypesApi.update(type.id, type);
      showMessage(`Tipo ${type.name} actualizado`);
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  // User management handlers
  const handleCreateUser = async () => {
    if (!userForm.username || !userForm.password || !userForm.name) {
      showMessage('Complete todos los campos', true); return;
    }
    setSaving(true);
    try {
      await usersApi.create(userForm);
      await loadAllData();
      setShowUserForm(false);
      setUserForm({ username: '', password: '', name: '', role: 'operator' });
      showMessage('Usuario creado');
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      const updateData = { ...userForm };
      if (!updateData.password) delete updateData.password;
      await usersApi.update(editingUser.id, updateData);
      await loadAllData();
      setEditingUser(null);
      setUserForm({ username: '', password: '', name: '', role: 'operator' });
      showMessage('Usuario actualizado');
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await usersApi.delete(userId);
      await loadAllData();
      showMessage('Usuario eliminado');
    } catch (err) { showMessage(formatApiError(err), true); }
  };

  const startEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ username: u.username, password: '', name: u.name, role: u.role });
    setShowUserForm(true);
  };

  // Product management handlers
  const handleCreateProduct = async () => {
    if (!productForm.name || productForm.price <= 0) {
      showMessage('Ingrese nombre y precio valido', true); return;
    }
    setSaving(true);
    try {
      await productsApi.create(productForm);
      await loadAllData();
      setShowProductForm(false);
      setProductForm({ name: '', price: 0, category: 'product', active: true, display_order: 0 });
      showMessage('Producto creado');
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  const handleUpdateProduct = async () => {
    setSaving(true);
    try {
      await productsApi.update(editingProduct.id, productForm);
      await loadAllData();
      setEditingProduct(null);
      setShowProductForm(false);
      setProductForm({ name: '', price: 0, category: 'product', active: true, display_order: 0 });
      showMessage('Producto actualizado');
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  const handleToggleProduct = async (product) => {
    try {
      if (product.active) {
        await productsApi.deactivate(product.id);
      } else {
        await productsApi.update(product.id, { active: true });
      }
      await loadAllData();
      showMessage(product.active ? 'Producto desactivado' : 'Producto activado');
    } catch (err) { showMessage(formatApiError(err), true); }
  };

  const startEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({ name: p.name, price: p.price, category: p.category, active: p.active, display_order: p.display_order || 0 });
    setShowProductForm(true);
  };

  // Report handlers
  const loadReports = async () => {
    setLoading(true);
    try {
      const params = { period: reportPeriod };
      if (reportPeriod === 'custom' && customStartDate && customEndDate) {
        params.start_date = new Date(customStartDate).toISOString();
        params.end_date = new Date(customEndDate).toISOString();
      }
      if (filterOperator) params.operator = filterOperator;
      if (filterVehicleType) params.vehicle_type = filterVehicleType;
      const [reportsRes, detailedRes] = await Promise.all([
        reportsApi.get(params),
        reportsApi.getDetailed(params)
      ]);
      setReports(reportsRes.data);
      setDetailedReports(detailedRes.data);
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setLoading(false); }
  };

  const exportReports = async () => {
    try {
      const params = {};
      if (reportPeriod === 'custom' && customStartDate && customEndDate) {
        params.start_date = new Date(customStartDate).toISOString();
        params.end_date = new Date(customEndDate).toISOString();
      }
      const res = await reportsApi.export(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_estacionamiento.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { showMessage(formatApiError(err), true); }
  };

  const handleSystemReset = async () => {
    if (!window.confirm('REINICIAR EL SISTEMA? Esto creara un respaldo y eliminara todas las transacciones y vehiculos actuales.')) return;
    if (!window.confirm('ESTA SEGURO? Esta accion no se puede deshacer.')) return;
    setSaving(true);
    try {
      const res = await systemApi.reset();
      showMessage(`Sistema reiniciado. ${res.data.transactions_backed_up} transacciones respaldadas.`);
      await loadAllData();
    } catch (err) { showMessage(formatApiError(err), true); }
    finally { setSaving(false); }
  };

  const uniqueOperators = [...new Set(users.map(u => u.name))];

  if (loading && !settings.business_name) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  // Revenue period card component
  const RevenuePeriodCard = ({ title, data }) => (
    <div className="bg-[#141414] border border-[#27272A] rounded-xl p-6">
      <h3 className="text-lg font-heading text-white mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-[#16A34A]" weight="bold" />
        {title}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#1C1C1E] rounded-lg p-4">
          <p className="text-[#A1A1AA] text-sm mb-1">Total</p>
          <p className="text-3xl font-heading text-[#4ADE80]">{formatCurrency(data.total)}</p>
        </div>
        <div className="bg-[#1C1C1E] rounded-lg p-4">
          <p className="text-[#A1A1AA] text-sm mb-1">Estacionamiento</p>
          <p className="text-2xl font-heading text-white">{formatCurrency(data.parking)}</p>
        </div>
        <div className="bg-[#1C1C1E] rounded-lg p-4">
          <p className="text-[#A1A1AA] text-sm mb-1">Productos/Servicios</p>
          <p className="text-2xl font-heading text-[#60A5FA]">{formatCurrency(data.products_services)}</p>
        </div>
        <div className="bg-[#1C1C1E] rounded-lg p-4">
          <p className="text-[#A1A1AA] text-sm mb-1">Transacciones</p>
          <p className="text-2xl font-heading text-white">{data.transactions}</p>
        </div>
      </div>
      {/* Product breakdown */}
      {data.products_breakdown && data.products_breakdown.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {data.products_breakdown.map((pb, idx) => (
            <span key={idx} className="px-3 py-1 bg-[#1C1C1E] border border-[#27272A] rounded-lg text-sm">
              <span className="text-[#A1A1AA]">{pb.name}{pb.units ? ` (${pb.units}u)` : ''}: </span>
              <span className="text-[#60A5FA] font-mono">{formatCurrency(pb.amount)}</span>
            </span>
          ))}
        </div>
      )}
      {/* Payment method breakdown */}
      {data.by_payment_method && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="payment-method-breakdown">
          <div className="bg-[#052E16] border border-[#16A34A]/30 rounded-lg p-2.5 text-center">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-wide">Efectivo</p>
            <p className="text-base font-mono text-[#4ADE80] font-bold" data-testid="pm-cash">{formatCurrency(data.by_payment_method.cash || 0)}</p>
          </div>
          <div className="bg-[#1E3A5F]/40 border border-[#2563EB]/30 rounded-lg p-2.5 text-center">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-wide">Tarjeta/Transf.</p>
            <p className="text-base font-mono text-[#60A5FA] font-bold" data-testid="pm-card">{formatCurrency(data.by_payment_method.card_transfer || 0)}</p>
          </div>
          <div className="bg-[#1C1C1E] border border-[#27272A] rounded-lg p-2.5 text-center">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-wide">No especificado</p>
            <p className="text-base font-mono text-white font-bold" data-testid="pm-unspecified">{formatCurrency(data.by_payment_method.unspecified || 0)}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#141414] border-b border-[#27272A] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="p-2 hover:bg-[#27272A] rounded-lg transition-colors" data-testid="back-to-kiosk">
              <ArrowLeft className="w-6 h-6 text-white" weight="bold" />
            </a>
            <div>
              <h1 className="text-lg font-heading text-white">Panel de Administracion</h1>
              <div className="flex items-center gap-3">
                <p className="text-sm text-[#A1A1AA]">{user?.name}</p>
                <ConnectionIndicator status={realtimeStatus} />
              </div>
            </div>
          </div>
        </div>
      </header>

      {(error || success) && (
        <div className={`mx-4 mt-4 p-3 rounded-lg ${error ? 'bg-[#450A0A] text-[#FCA5A5]' : 'bg-[#052E16] text-[#4ADE80]'}`}>
          {error || success}
        </div>
      )}

      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-5 sm:grid-cols-10 bg-[#141414] border border-[#27272A] h-auto sm:h-14 mb-4">
            <TabsTrigger value="revenue" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-revenue">
              <TrendUp className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Ingresos</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-products">
              <ShoppingCart className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Productos</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-settings">
              <Gear className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Config</span>
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-pricing">
              <CurrencyCircleDollar className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Precios</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-users">
              <Users className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Usuarios</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-reports">
              <ChartBar className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Reportes</span>
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-audit">
              <ClockCounterClockwise className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Auditoria</span>
            </TabsTrigger>
            <TabsTrigger value="diagnostics" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-diagnostics">
              <Printer className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">POS Diag</span>
            </TabsTrigger>
            <TabsTrigger value="corrections" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-corrections">
              <ArrowCounterClockwise className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Correcciones</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-[#27272A] h-12 text-white" data-testid="tab-templates">
              <Article className="w-5 h-5 mr-1" weight="bold" />
              <span className="hidden sm:inline text-xs">Plantillas</span>
            </TabsTrigger>
          </TabsList>

          {/* Revenue Dashboard Tab */}
          <TabsContent value="revenue" className="space-y-4">
            {revenueDashboard && (
              <>
                <RevenuePeriodCard title="Ingresos de Hoy" data={revenueDashboard.today} />
                <RevenuePeriodCard title="Ultimos 7 Dias" data={revenueDashboard.week} />
                <RevenuePeriodCard title="Ultimos 30 Dias" data={revenueDashboard.month} />
              </>
            )}
          </TabsContent>

          {/* Products & Services Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-heading text-white">Productos y Servicios</h3>
              <button
                onClick={() => { setShowProductForm(true); setEditingProduct(null); setProductForm({ name: '', price: 0, category: 'product', active: true, display_order: 0 }); }}
                className="btn-success px-4"
                data-testid="add-product-btn"
              >
                <Plus className="w-5 h-5 mr-2" weight="bold" />
                Nuevo
              </button>
            </div>

            {/* Product Form Modal */}
            {showProductForm && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                <div className="bg-[#141414] rounded-xl w-full max-w-md border border-[#27272A]">
                  <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
                    <h3 className="text-lg font-heading text-white">
                      {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                    </h3>
                    <button onClick={() => setShowProductForm(false)} className="p-2 hover:bg-[#27272A] rounded-lg">
                      <X className="w-5 h-5 text-[#A1A1AA]" weight="bold" />
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Nombre</label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none"
                        placeholder="Ej: Agua, Jugo, Snack..."
                        data-testid="product-form-name"
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Precio (CLP)</label>
                      <input
                        type="number"
                        value={productForm.price}
                        onChange={(e) => setProductForm(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none"
                        data-testid="product-form-price"
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Categoria</label>
                      <select
                        value={productForm.category}
                        onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none"
                        data-testid="product-form-category"
                      >
                        <option value="product">Producto</option>
                        <option value="service">Servicio</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Orden de visualizacion</label>
                      <input
                        type="number"
                        value={productForm.display_order}
                        onChange={(e) => setProductForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none"
                        data-testid="product-form-order"
                      />
                    </div>
                  </div>
                  <div className="p-4 border-t border-[#27272A]">
                    <button
                      onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                      disabled={saving}
                      className="w-full btn-success"
                      data-testid="product-form-submit"
                    >
                      {saving ? 'Guardando...' : (editingProduct ? 'Actualizar' : 'Crear Producto')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Products List */}
            <div className="bg-[#141414] border border-[#27272A] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1C1C1E]">
                  <tr>
                    <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Nombre</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Precio</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Categoria</th>
                    <th className="text-center text-[#A1A1AA] text-sm font-medium p-4">Estado</th>
                    <th className="text-center text-[#A1A1AA] text-sm font-medium p-4">Orden</th>
                    <th className="text-right text-[#A1A1AA] text-sm font-medium p-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className={`border-t border-[#27272A] ${!p.active ? 'opacity-50' : ''}`}>
                      <td className="p-4 text-white font-medium">{p.name}</td>
                      <td className="p-4 text-[#4ADE80] font-mono">{formatCurrency(p.price)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          p.category === 'service' ? 'bg-[#1e3a5f] text-[#60A5FA]' : 'bg-[#422006] text-[#FCD34D]'
                        }`}>
                          {p.category === 'service' ? 'Servicio' : 'Producto'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => handleToggleProduct(p)}
                          className="inline-flex items-center"
                          data-testid={`toggle-product-${p.name}`}
                        >
                          {p.active ? (
                            <ToggleRight className="w-8 h-8 text-[#16A34A]" weight="fill" />
                          ) : (
                            <ToggleLeft className="w-8 h-8 text-[#71717A]" weight="fill" />
                          )}
                        </button>
                      </td>
                      <td className="p-4 text-center text-[#A1A1AA] font-mono">{p.display_order || 0}</td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => startEditProduct(p)}
                          className="p-2 hover:bg-[#27272A] rounded-lg mr-1"
                          data-testid={`edit-product-${p.name}`}
                        >
                          <Pencil className="w-5 h-5 text-[#A1A1AA]" weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <div className="bg-[#141414] border border-[#27272A] rounded-xl p-6">
              <h3 className="text-lg font-heading text-white mb-4">Configuracion General</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Nombre del Negocio</label>
                  <input type="text" value={settings.business_name || ''} onChange={(e) => handleSettingsChange('business_name', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-business-name" />
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Total de Espacios</label>
                  <div className="flex gap-2">
                    <input type="number" value={settings.total_spaces || 12} onChange={(e) => handleSettingsChange('total_spaces', parseInt(e.target.value))} className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-total-spaces" />
                    <button onClick={regenerateSpaces} className="btn-secondary px-4" data-testid="regenerate-spaces-btn">Regenerar</button>
                  </div>
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Hora de Apertura</label>
                  <input type="time" value={settings.open_time || '08:00'} onChange={(e) => handleSettingsChange('open_time', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-open-time" />
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Hora de Cierre</label>
                  <input type="time" value={settings.close_time || '22:00'} onChange={(e) => handleSettingsChange('close_time', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-close-time" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[#A1A1AA] text-sm mb-2">Mensaje de Cabecera (Ticket)</label>
                  <input type="text" value={settings.ticket_header || ''} onChange={(e) => handleSettingsChange('ticket_header', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-ticket-header" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[#A1A1AA] text-sm mb-2">Mensaje de Pie (Ticket)</label>
                  <input type="text" value={settings.ticket_footer || ''} onChange={(e) => handleSettingsChange('ticket_footer', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-ticket-footer" />
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Modo del Dispositivo</label>
                  <select value={settings.device_mode || 'desktop'} onChange={(e) => handleSettingsChange('device_mode', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="settings-device-mode">
                    <option value="desktop">Escritorio / Laptop</option>
                    <option value="mobile">Celular / Tablet</option>
                    <option value="pos_terminal">Terminal POS (impresion directa)</option>
                  </select>
                </div>
              </div>
              <button onClick={saveSettings} disabled={saving} className="mt-4 btn-success px-6" data-testid="save-settings-btn">
                {saving ? 'Guardando...' : 'Guardar Configuracion'}
              </button>
            </div>
            {/* System Reset */}
            <div className="bg-[#450A0A] border border-[#DC2626] rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Warning className="w-8 h-8 text-[#FCA5A5] flex-shrink-0" weight="bold" />
                <div>
                  <h3 className="text-lg font-heading text-[#FCA5A5] mb-2">Reinicio del Sistema</h3>
                  <p className="text-[#FCA5A5]/80 text-sm mb-4">Esta accion generara un respaldo y eliminara todas las transacciones, vehiculos y registros de auditoria.</p>
                  <button onClick={handleSystemReset} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold px-6 py-3 rounded-lg transition-colors" data-testid="system-reset-btn">Reiniciar Sistema</button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-4">
            {vehicleTypes.map((type) => {
              const Icon = VEHICLE_ICONS[type.name] || CarSimple;
              return (
                <div key={type.id} className="bg-[#141414] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="w-8 h-8 text-[#16A34A]" weight="duotone" />
                    <h3 className="text-lg font-heading text-white">{type.name}</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={type.is_flat_rate || false} onChange={(e) => handleVehicleTypeChange(type.id, 'is_flat_rate', e.target.checked)} className="w-5 h-5" data-testid={`pricing-${type.name}-flat-rate`} />
                      <label className="text-white">Tarifa Fija</label>
                    </div>
                    {type.is_flat_rate ? (
                      <div>
                        <label className="block text-[#A1A1AA] text-sm mb-2">Tarifa Fija (CLP)</label>
                        <input type="number" value={type.flat_rate || 0} onChange={(e) => handleVehicleTypeChange(type.id, 'flat_rate', parseInt(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid={`pricing-${type.name}-flat-rate-amount`} />
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-3">
                        <div>
                          <label className="block text-[#A1A1AA] text-sm mb-2">Minutos Base</label>
                          <input type="number" value={type.base_minutes || 0} onChange={(e) => handleVehicleTypeChange(type.id, 'base_minutes', parseInt(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[#A1A1AA] text-sm mb-2">Precio Base (CLP)</label>
                          <input type="number" value={type.base_price || 0} onChange={(e) => handleVehicleTypeChange(type.id, 'base_price', parseInt(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[#A1A1AA] text-sm mb-2">Precio Extra/Min (CLP)</label>
                          <input type="number" value={type.extra_per_minute || 0} onChange={(e) => handleVehicleTypeChange(type.id, 'extra_per_minute', parseInt(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[#A1A1AA] text-sm mb-2">Periodo de Gracia (min)</label>
                          <input type="number" value={type.grace_period || 0} onChange={(e) => handleVehicleTypeChange(type.id, 'grace_period', parseInt(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[#A1A1AA] text-sm mb-2">Cargo Minimo (CLP)</label>
                          <input type="number" value={type.minimum_charge || 0} onChange={(e) => handleVehicleTypeChange(type.id, 'minimum_charge', parseInt(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[#A1A1AA] text-sm mb-2">Redondeo</label>
                          <select value={type.rounding_rule || 'exact'} onChange={(e) => handleVehicleTypeChange(type.id, 'rounding_rule', e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none">
                            <option value="exact">Exacto</option>
                            <option value="round_up">Redondear arriba</option>
                          </select>
                        </div>
                      </div>
                    )}
                    <button onClick={() => saveVehicleType(type)} disabled={saving} className="btn-success px-6" data-testid={`save-pricing-${type.name}`}>
                      Guardar {type.name}
                    </button>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-heading text-white">Gestion de Usuarios</h3>
              <button onClick={() => { setShowUserForm(true); setEditingUser(null); setUserForm({ username: '', password: '', name: '', role: 'operator' }); }} className="btn-success px-4" data-testid="add-user-btn">
                <Plus className="w-5 h-5 mr-2" weight="bold" />
                Nuevo Usuario
              </button>
            </div>
            {showUserForm && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                <div className="bg-[#141414] rounded-xl w-full max-w-md border border-[#27272A]">
                  <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
                    <h3 className="text-lg font-heading text-white">{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
                    <button onClick={() => setShowUserForm(false)} className="p-2 hover:bg-[#27272A] rounded-lg">
                      <X className="w-5 h-5 text-[#A1A1AA]" weight="bold" />
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Usuario</label>
                      <input type="text" value={userForm.username} onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="user-form-username" />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Contrasena {editingUser && '(dejar vacio para no cambiar)'}</label>
                      <input type="password" value={userForm.password} onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="user-form-password" />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Nombre</label>
                      <input type="text" value={userForm.name} onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="user-form-name" />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Rol</label>
                      <select value={userForm.role} onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#16A34A] focus:outline-none" data-testid="user-form-role">
                        <option value="operator">Operador</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-4 border-t border-[#27272A]">
                    <button onClick={editingUser ? handleUpdateUser : handleCreateUser} disabled={saving} className="w-full btn-success" data-testid="user-form-submit">
                      {saving ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear Usuario')}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="bg-[#141414] border border-[#27272A] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1C1C1E]">
                  <tr>
                    <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Usuario</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Nombre</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Rol</th>
                    <th className="text-right text-[#A1A1AA] text-sm font-medium p-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-[#27272A]">
                      <td className="p-4 text-white font-mono">{u.username}</td>
                      <td className="p-4 text-white">{u.name}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${u.role === 'admin' ? 'bg-[#422006] text-[#FCD34D]' : u.role === 'supervisor' ? 'bg-[#1e3a5f] text-[#60A5FA]' : 'bg-[#052E16] text-[#4ADE80]'}`}>
                          {u.role === 'admin' ? 'Admin' : u.role === 'supervisor' ? 'Supervisor' : 'Operador'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <button onClick={() => startEditUser(u)} className="p-2 hover:bg-[#27272A] rounded-lg mr-2" data-testid={`edit-user-${u.username}`}>
                          <Pencil className="w-5 h-5 text-[#A1A1AA]" weight="bold" />
                        </button>
                        <button onClick={() => handleDeleteUser(u.id)} className="p-2 hover:bg-[#450A0A] rounded-lg" data-testid={`delete-user-${u.username}`}>
                          <Trash className="w-5 h-5 text-[#FCA5A5]" weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <div className="bg-[#141414] border border-[#27272A] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <Funnel className="w-5 h-5 text-[#A1A1AA]" weight="bold" />
                <span className="text-white font-medium">Filtros</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[#A1A1AA] text-xs mb-1">Periodo</label>
                  <select value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm focus:border-[#16A34A] focus:outline-none" data-testid="report-period">
                    <option value="daily">Hoy</option>
                    <option value="weekly">Ultima Semana</option>
                    <option value="monthly">Ultimo Mes</option>
                    <option value="custom">Personalizado</option>
                  </select>
                </div>
                {reportPeriod === 'custom' && (
                  <>
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Desde</label>
                      <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm focus:border-[#16A34A] focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Hasta</label>
                      <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm focus:border-[#16A34A] focus:outline-none" />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-[#A1A1AA] text-xs mb-1">Operador</label>
                  <select value={filterOperator} onChange={(e) => setFilterOperator(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm focus:border-[#16A34A] focus:outline-none">
                    <option value="">Todos</option>
                    {uniqueOperators.map(op => <option key={op} value={op}>{op}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-xs mb-1">Tipo Vehiculo</label>
                  <select value={filterVehicleType} onChange={(e) => setFilterVehicleType(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm focus:border-[#16A34A] focus:outline-none">
                    <option value="">Todos</option>
                    {vehicleTypes.map(vt => <option key={vt.id} value={vt.name}>{vt.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={loadReports} className="btn-primary px-6" data-testid="load-reports-btn">Cargar</button>
                <button onClick={exportReports} className="btn-secondary px-6" data-testid="export-reports-btn">
                  <Download className="w-5 h-5 mr-2" weight="bold" />
                  Exportar CSV
                </button>
              </div>
            </div>

            {reports && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Vehiculos</p>
                    <p className="text-2xl font-heading text-white" data-testid="report-total-vehicles">{reports.total_vehicles}</p>
                  </div>
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Total Ingresos</p>
                    <p className="text-2xl font-heading text-[#4ADE80]" data-testid="report-total-revenue">{formatCurrency(reports.total_revenue)}</p>
                  </div>
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Estacionamiento</p>
                    <p className="text-2xl font-heading text-white">{formatCurrency(reports.parking_revenue)}</p>
                  </div>
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Productos/Servicios</p>
                    <p className="text-2xl font-heading text-[#60A5FA]">{formatCurrency(reports.products_services_revenue)}</p>
                  </div>
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Tiempo Promedio</p>
                    <p className="text-2xl font-heading text-white">{formatDuration(Math.round(reports.average_time))}</p>
                  </div>
                </div>
                
                {/* Product Breakdown */}
                {reports.products_breakdown && reports.products_breakdown.length > 0 && (
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-6">
                    <h3 className="text-lg font-heading text-white mb-4">Desglose por Producto/Servicio</h3>
                    <div className="space-y-2">
                      {reports.products_breakdown.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-[#1C1C1E] rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-white">{item.name}</span>
                            {item.units > 0 && <span className="text-[#A1A1AA] text-sm">({item.units} unidades)</span>}
                          </div>
                          <span className="font-mono text-xl text-[#60A5FA]">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detailed Table */}
                {detailedReports && detailedReports.transactions?.length > 0 && (
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-[#27272A]">
                      <h3 className="text-lg font-heading text-white">Detalle de Transacciones</h3>
                      <p className="text-sm text-[#A1A1AA]">{detailedReports.total_count} registros</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[800px]">
                        <thead className="bg-[#1C1C1E]">
                          <tr>
                            <th className="text-left text-[#A1A1AA] text-xs font-medium p-3">Fecha/Hora</th>
                            <th className="text-left text-[#A1A1AA] text-xs font-medium p-3">Ticket</th>
                            <th className="text-left text-[#A1A1AA] text-xs font-medium p-3">Espacio</th>
                            <th className="text-left text-[#A1A1AA] text-xs font-medium p-3">Patente</th>
                            <th className="text-left text-[#A1A1AA] text-xs font-medium p-3">Tipo</th>
                            <th className="text-right text-[#A1A1AA] text-xs font-medium p-3">Parking</th>
                            <th className="text-right text-[#A1A1AA] text-xs font-medium p-3">Extras</th>
                            <th className="text-right text-[#A1A1AA] text-xs font-medium p-3">Total</th>
                            <th className="text-left text-[#A1A1AA] text-xs font-medium p-3">Operador</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailedReports.transactions.map((t) => (
                            <tr key={t.id} className="border-t border-[#27272A] hover:bg-[#1C1C1E]">
                              <td className="p-3 text-white text-xs font-mono">{formatDateTime(t.exit_time)}</td>
                              <td className="p-3 text-white text-xs font-mono">{t.ticket_id}</td>
                              <td className="p-3 text-white text-xs">{t.space_label}</td>
                              <td className="p-3 text-white text-xs font-mono">{t.plate_number}</td>
                              <td className="p-3 text-white text-xs">{t.vehicle_type}</td>
                              <td className="p-3 text-white text-xs font-mono text-right">{formatCurrency(t.parking_amount)}</td>
                              <td className="p-3 text-[#60A5FA] text-xs font-mono text-right">
                                {(t.extras_added_to_total > 0 || t.extras_paid_separately > 0) ? formatCurrency(t.extras_added_to_total + t.extras_paid_separately) : '-'}
                              </td>
                              <td className="p-3 text-[#4ADE80] text-xs font-mono text-right font-bold">{formatCurrency(t.total_price)}</td>
                              <td className="p-3 text-[#A1A1AA] text-xs">{t.check_out_operator}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {reports.revenue_by_type?.length > 0 && (
                  <div className="bg-[#141414] border border-[#27272A] rounded-xl p-6">
                    <h3 className="text-lg font-heading text-white mb-4">Ingresos por Tipo de Vehiculo</h3>
                    <div className="space-y-3">
                      {reports.revenue_by_type.map((item, idx) => {
                        const Icon = VEHICLE_ICONS[item.type] || CarSimple;
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 bg-[#1C1C1E] rounded-lg">
                            <div className="flex items-center gap-3">
                              <Icon className="w-6 h-6 text-[#16A34A]" weight="duotone" />
                              <span className="text-white">{item.type}</span>
                              <span className="text-[#A1A1AA] text-sm">({item.count} vehiculos)</span>
                            </div>
                            <span className="font-mono text-xl text-[#4ADE80]">{formatCurrency(item.revenue)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit" className="space-y-4">
            <div className="bg-[#141414] border border-[#27272A] rounded-xl overflow-hidden">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-[#1C1C1E] sticky top-0">
                    <tr>
                      <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Fecha/Hora</th>
                      <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Usuario</th>
                      <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Accion</th>
                      <th className="text-left text-[#A1A1AA] text-sm font-medium p-4">Detalles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="border-t border-[#27272A]">
                        <td className="p-4 text-white font-mono text-sm">{formatDateTime(log.timestamp)}</td>
                        <td className="p-4 text-white">{log.username}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.action === 'check_in' ? 'bg-[#052E16] text-[#4ADE80]' :
                            log.action === 'check_out' ? 'bg-[#422006] text-[#FCD34D]' :
                            log.action === 'login' ? 'bg-[#1e3a5f] text-[#60A5FA]' :
                            log.action === 'system_reset' ? 'bg-[#450A0A] text-[#FCA5A5]' :
                            log.action.includes('service_charge') ? 'bg-[#1e3a5f] text-[#60A5FA]' :
                            log.action.includes('voided') ? 'bg-[#450A0A] text-[#FCA5A5]' :
                            'bg-[#27272A] text-white'
                          }`}>{log.action}</span>
                        </td>
                        <td className="p-4 text-[#A1A1AA] text-sm font-mono">
                          {log.details?.plate_number && `Patente: ${log.details.plate_number}`}
                          {log.details?.space_label && ` | Espacio: ${log.details.space_label}`}
                          {log.details?.total_price !== undefined && ` | Total: ${formatCurrency(log.details.total_price)}`}
                          {log.details?.item_name && ` | Producto: ${log.details.item_name}`}
                          {log.details?.amount !== undefined && ` | Monto: ${formatCurrency(log.details.amount)}`}
                          {log.details?.void_reason && ` | Razon: ${log.details.void_reason}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* POS Diagnostics Tab */}
          <TabsContent value="diagnostics" className="space-y-4">
            <PosDiagnostics />
          </TabsContent>

          {/* Exit Corrections Tab */}
          <TabsContent value="corrections" className="space-y-4">
            <AdminCorrections />
          </TabsContent>

          {/* Ticket Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <AdminTicketDesigner />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
